import { DatePipe } from '@angular/common';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { Timesheet } from 'src/app/models/Timesheet';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/dialog.component';
import { JiraLoginService } from 'src/app/shared/login-components/jira-login/jira-login.service';

import { AddTimesheetModalComponent } from '../add-timesheet-modal/add-timesheet-modal.component';
import { SdworxUploadModalComponent } from '../sdworx-upload-modal/sdworx-upload-modal.component';
import { ChangeSelectedDate, FetchTimesheets } from '../timesheets.actions';
import { TimesheetState } from '../timesheets.state';

@Component({
    selector: 'app-timesheets-overview',
    templateUrl: './timesheets-overview.component.html',
    styleUrls: ['./timesheets-overview.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class TimesheetsOverviewComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Select(TimesheetState.selectedDate) selectedDate$: Observable<string>;
    @Select(TimesheetState.timesheets) timesheets$: Observable<Timesheet[]>;

    selectedDate: string;
    currentDate: string;
    isLoading = true;
    employees = [];
    currentTimesheets: Timesheet[] = [];
    sortedByEmployee = true;
    sortedByProject = false;
    showOnlyDoneTimesheets = true;

    constructor(
        private store: Store,
        private modalService: NgbModal,
        private logger: NGXLogger,
        private jiraLoginService: JiraLoginService,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private datePipe: DatePipe,
        private route: ActivatedRoute,
        private location: Location,
    ) {}

    ngOnInit(): void {
        this.currentDate = moment().format('MMMM yyyy');
        this.logger.debug('Fetching timesheets');

        // Restore tab from query parameter (and react to changes while on the same route)
        this.subscriptions.add(
            this.route.queryParamMap.subscribe((params) => {
                const tabParam = params.get('tab');
                if (tabParam === 'invoices') {
                    this.sortedByEmployee = false;
                    this.sortedByProject = true;
                } else {
                    this.sortedByEmployee = true;
                    this.sortedByProject = false;
                }
            }),
        );

        this.subscriptions.add(
            this.selectedDate$.subscribe((s) => {
                this.selectedDate = s;
            }),
        );

        this.store.dispatch(new FetchTimesheets());

        this.subscriptions.add(
            this.timesheets$.subscribe((timesheets) => {
                if (timesheets) {
                    this.currentTimesheets = timesheets;
                    this.employees = [];
                    timesheets.forEach((el) => this.employees.push(el.employee));
                    if (
                        timesheets.every(
                            (t) => moment(new Date(t.expirationDate)).format('YYYY-MM') == moment(new Date(this.selectedDate)).format('YYYY-MM'),
                        ) ||
                        timesheets.length == 0
                    ) {
                        this.isLoading = false;
                    }
                }
            }),
        );
    }

    byEmployeeSelected() {
        this.sortedByEmployee = true;
        this.sortedByProject = false;
        this.updateTabQueryParam('timesheets');
    }

    byProjectSelected() {
        this.sortedByEmployee = false;
        this.sortedByProject = true;
        this.updateTabQueryParam('invoices');
    }

    private updateTabQueryParam(tab: string) {
        const url = this.route.snapshot.url.map((s) => s.path).join('/');
        const basePath = '/timesheets/overview';
        this.location.replaceState(basePath, `tab=${tab}`);
    }

    setShowOnlyDoneTimesheets(onlyDone: boolean) {
        this.showOnlyDoneTimesheets = onlyDone;
    }

    hasTimesheetsNotDone(): boolean {
        return (this.currentTimesheets || []).some((timesheet) => timesheet?.status?.key !== 'DONE');
    }

    getTimesheetsNotDoneEmployees(): string[] {
        const names = (this.currentTimesheets || [])
            .filter((timesheet) => timesheet?.status?.key !== 'DONE')
            .map((timesheet) => {
                const firstName = timesheet?.employee?.firstName || '';
                const lastName = timesheet?.employee?.lastName || '';
                return `${firstName} ${lastName}`.trim();
            })
            .filter((name) => !!name);

        return Array.from(new Set(names)).sort((nameA, nameB) => nameA.localeCompare(nameB));
    }

    moveDate(amount: number) {
        this.isLoading = true;
        this.logger.debug('Changing selected date & refetch timesheets');
        this.subscriptions.add(
            this.store.dispatch(new ChangeSelectedDate(amount)).subscribe(() => {
                this.store.dispatch(new FetchTimesheets());
            }),
        );
    }

    addNewTimesheet() {
        this.logger.debug('Opening addTimesheetModal');
        const modalRef = this.modalService.open(AddTimesheetModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.currentEmployees = this.employees;
    }

    openSdworxUploadModal() {
        const ts = this.store.selectSnapshot(TimesheetState.timesheets) || [];
        const date = new Date(this.selectedDate);
        const label = this.datePipe.transform(date, 'MMMM yyyy') || '';
        const modalRef = this.modalService.open(SdworxUploadModalComponent, {
            windowClass: 'modal-prompt modal-prompt--min-content',
            animation: false,
            size: 'xl',
        });
        modalRef.componentInstance.timesheets = ts;
        modalRef.componentInstance.selectedDateLabel = label;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    importFromJiraForSelectedMonth() {
        const selectedDateSplit = this.selectedDate.split('-');
        const year = parseInt(selectedDateSplit[0]);
        const month = parseInt(selectedDateSplit[1]);
        const date = new Date(this.selectedDate);
        const formattedDate = this.datePipe.transform(date, 'MMMM yyyy');

        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Import Timesheets from Jira',
                    question: `<br>Are you sure you want to import all timesheets from Jira for <strong>${formattedDate}</strong>?<br><br>
                    All timesheets for linked employees and projects will be updated<br>`,
                },
            })
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result === true) {
                    this.jiraLoginService.importAllTempoTimeSheets(month, year).subscribe(
                        (response) => {
                            this.logger.debug('Successfully synchronized with Jira');
                            this.toastr.success('Successfully synchronized with Jira.');

                            this.store.dispatch(new FetchTimesheets());
                        },
                        (error) => {
                            this.logger.error('Failed to sync with Jira');
                            this.toastr.error('Oops, something went wrong. Please try again later.');
                        },
                    );
                }
            });
    }

    refresh() {
        this.isLoading = true;
        this.store.dispatch(new FetchTimesheets());
    }
}
